import { Assignment, AssignmentTurnedIn, Lan } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { buildCarrierStats } from "components/CarrierStatsRow/utils";
import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { STAT_ICON_PURPLE } from "constants/index";
import { useCarrierStats } from "hooks/useCarrierStats";

import { tileColumnWidth } from "./utils";

import type { ReactElement, ReactNode } from "react";

interface Props {
  companyId: number;
}

interface StatTile {
  icon: ReactNode;
  label: string;
  value: string;
  countLabel?: string;
  tooltip?: string;
}

interface CarrierStatCardProps {
  stat: StatTile;
  columnWidth: number;
}

interface MaybeTooltipProps {
  title?: string;
  children: ReactElement;
}

function MaybeTooltip(props: MaybeTooltipProps) {
  const { title, children } = props;

  return title ? <Tooltip title={title}>{children}</Tooltip> : children;
}

function CarrierStatCard(props: CarrierStatCardProps) {
  const { stat, columnWidth } = props;

  return (
    <Grid item xs={12} sm={6} md={columnWidth}>
      <MaybeTooltip title={stat.tooltip}>
        <Card sx={{ height: "100%" }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              {stat.icon}
              <Box>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h5" color="text.secondary">
                    {stat.label}
                  </Typography>
                  <Typography variant="h5">{stat.value}</Typography>
                </Stack>
                {stat.countLabel && (
                  <Typography variant="body2" color="text.secondary">
                    {stat.countLabel}
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </MaybeTooltip>
    </Grid>
  );
}

export function CarrierStatsCards(props: Props) {
  const { companyId } = props;
  const { carrierMeta, loading, error } = useCarrierStats({ companyId });

  const stats: StatTile[] = carrierMeta
    ? [
        {
          icon: <Assignment fontSize="large" color="primary" />,
          label: "Quotes Created:",
          value: carrierMeta.totalQuotesCreated.toLocaleString(),
        },
        {
          icon: <AssignmentTurnedIn fontSize="large" color="success" />,
          label: "Shipments Booked:",
          value: carrierMeta.totalShipmentsBooked.toLocaleString(),
        },
        {
          icon: <Lan fontSize="large" sx={{ color: STAT_ICON_PURPLE }} />,
          label: "In Shipper Networks:",
          value: carrierMeta.currentShipperNetworkSize.toLocaleString(),
        },
        // Rating, satisfaction and tracking links moved here from the header.
        ...buildCarrierStats({
          carrierMeta,
          withTrackingLinkSubmissionRate: true,
        }).map((stat) => ({
          icon: <stat.icon fontSize="large" sx={{ color: stat.iconColor }} />,
          label: stat.description,
          value: stat.value,
          countLabel: stat.countLabel,
          tooltip: stat.tooltip,
        })),
      ]
    : [];

  return (
    <LoadingWrappedNode loading={loading}>
      <ErrorTypography error={error} />
      {carrierMeta && (
        <Box sx={{ my: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
            Performance on Evotrux
          </Typography>
          <EvotruxDivider />

          <Grid container spacing={2} sx={{ mt: 2 }}>
            {stats.map((stat, index) => (
              <CarrierStatCard
                key={stat.label}
                stat={stat}
                columnWidth={tileColumnWidth({
                  tileCount: stats.length,
                  index,
                })}
              />
            ))}
          </Grid>
        </Box>
      )}
    </LoadingWrappedNode>
  );
}
